import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  getTeacherProfile,
  recordProbation,
  TeacherIncidentKind,
  TeacherOperationalProfile,
} from '../api/supply-health';
import {
  Empty,
  Field,
  Kpi,
  KpiRow,
  Note,
  OpsPage,
  Pill,
  Row,
  Section,
  Table,
  Value,
} from '../../../design-system/ops';
import { RatingLine } from './AdminTrustChain';

/**
 * One teacher's operational profile — ADM-09-FR-01, ADM-09-FR-05, ADM-09-FR-06.
 *
 * Every section is read from the record that owns it and links back to it:
 * the application to the applications queue, a course to its public page, a
 * sold commitment to its support case. Nothing on this screen is a copy the
 * console keeps (BR-A09-05).
 *
 * The one action is recording a probation, and only when the rating is under
 * the governed floor — the server refuses otherwise, and the button is not
 * offered, because a decision citing a policy that did not apply is worse
 * than no decision. The reason is required and written beside the button, not
 * in a prompt: this is a decision about someone's livelihood.
 */

const fmt = (iso?: string): string => (iso ? new Date(iso).toLocaleString('es-MX') : '—');

const INCIDENT_LABEL: Record<TeacherIncidentKind, string> = {
  TEACHER_CANCELLATION: 'Cancelación del profesor',
  TEACHER_NO_SHOW: 'No se presentó',
};

/** Minimum the screen asks for before offering the decision. */
const MIN_REASON = 5;

const AdminTeacherProfile: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const [profile, setProfile] = useState<TeacherOperationalProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ tone: 'plain' | 'bad'; text: string } | null>(null);

  const load = useCallback(async () => {
    if (!teacherId) return;
    try {
      setProfile(await getTeacherProfile(teacherId));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar el perfil operativo');
    }
  }, [teacherId]);

  useEffect(() => {
    load();
  }, [load]);

  const submitProbation = async () => {
    if (!teacherId) return;
    setBusy(true);
    setFlash(null);
    try {
      const { replayed } = await recordProbation(teacherId, reason.trim());
      setFlash({
        tone: 'plain',
        text: replayed
          ? 'Ya había una probation registrada sobre esta misma calificación; no se duplicó.'
          : 'Probation registrada en la auditoría con la versión del umbral vigente.',
      });
      setReason('');
      await load();
    } catch (e: any) {
      // The refusal says why — not under the floor, no reviews, no permission.
      // Shown verbatim so the operator does not have to ask an engineer.
      setFlash({ tone: 'bad', text: e?.message || 'No se pudo registrar la probation' });
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <OpsPage title="Perfil operativo">
        <Note tone="bad">{error}</Note>
      </OpsPage>
    );
  }

  if (!profile) {
    return (
      <OpsPage title="Perfil operativo">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  const { rating, incidents } = profile;

  return (
    <OpsPage title={profile.name} subtitle={<Link to="/admin/chain">← Trust Chain</Link>}>
      <KpiRow>
        <Kpi label="Cursos publicados" value={profile.publishedCourses} />
        <Kpi label="Inscripciones activas" value={profile.activeEnrollments} />
        <Kpi
          label="Cancelaciones del profesor"
          value={incidents.cancellations}
          tone={incidents.cancellations > 0 ? 'warn' : 'neutral'}
        />
        <Kpi
          label="Ausencias del profesor"
          value={incidents.noShows}
          tone={incidents.noShows > 0 ? 'bad' : 'neutral'}
        />
      </KpiRow>

      <Section title="Verificación y solicitud">
        {/* Printed as stored. Whether this teacher may take new enrolments is
            T03's decision, and this screen does not recompute it. */}
        <Field label="Estado de verificación (registro de usuario)">
          <Value>
            {profile.verificationStatus ?? '—'}
            {profile.verifiedAt ? ` · ${fmt(profile.verifiedAt)}` : ''}
          </Value>
        </Field>
        <Field label="Solicitud de incorporación">
          {profile.application ? (
            <Value>
              <Pill>{profile.application.state}</Pill> enviada{' '}
              {fmt(profile.application.submittedAt)}
              {profile.application.reviewedAt
                ? ` · revisada ${fmt(profile.application.reviewedAt)}`
                : ''}{' '}
              · <Link to="/admin/teacher-applications">Ver solicitudes</Link>
            </Value>
          ) : (
            <Value>Sin solicitud</Value>
          )}
        </Field>
      </Section>

      <Section title="Calificación">
        <Field label="Promedio de las familias">
          <Value>
            <RatingLine rating={rating} />
          </Value>
        </Field>
        <Field label="Calificación mínima">
          <Value>
            {rating.floor} ★ · {rating.floorVersion}
          </Value>
        </Field>
        {/* No cut-off of our own: the baseline sets no minimum number of
            reviews, so the count is stated and the judgement left to the
            operator rather than encoded as a threshold nobody decided. */}
        {rating.belowFloor ? (
          <Note tone="warn">
            El promedio se apoya en {rating.totalReviews} reseña
            {rating.totalReviews === 1 ? '' : 's'}. La regla no fija un mínimo de reseñas; tenlo en
            cuenta antes de decidir.
          </Note>
        ) : null}
      </Section>

      <Section title="Probation" count={profile.probations.length}>
        {profile.probations.length === 0 ? (
          <Empty icon="📋" title="Sin probation registrada" />
        ) : (
          <Table columns="160px 1fr 160px" head={['Fecha', 'Decisión', 'Umbral citado']}>
            {profile.probations.map((p) => (
              <Row key={p.auditId}>
                <span className="mut">{fmt(p.occurredAt)}</span>
                <span>
                  <b>{p.actorLabel}</b>: {p.reason}
                  <br />
                  <span className="mut">{p.objectLabel}</span>
                </span>
                <span className="mut">{p.parameterVersion ?? '—'}</span>
              </Row>
            ))}
          </Table>
        )}

        {rating.belowFloor ? (
          <div style={{ marginTop: 12 }}>
            <Note tone="warn">
              Bajo la calificación mínima (BR-24). Registrar la probation no pausa inscripciones ni
              afecta a las familias ya inscritas: deja la decisión auditada con su razón y la
              versión del umbral.
            </Note>
            <Field label="Razón">
              <textarea
                id="probation-reason"
                className="inp"
                rows={3}
                value={reason}
                aria-label="Razón de la probation"
                placeholder={`Mínimo ${MIN_REASON} caracteres. Queda en la auditoría.`}
                onChange={(ev) => setReason(ev.target.value)}
              />
            </Field>
            <button
              className="btn btn-d"
              disabled={busy || reason.trim().length < MIN_REASON}
              onClick={submitProbation}
            >
              {busy ? 'Registrando…' : 'Registrar probation'}
            </button>
          </div>
        ) : null}
        {flash ? <Note tone={flash.tone}>{flash.text}</Note> : null}
      </Section>

      <Section title="Cursos" count={profile.courses.length}>
        {profile.courses.length === 0 ? (
          <Empty icon="📚" title="Sin cursos" />
        ) : (
          <Table columns="1fr 140px 110px 120px" head={['Curso', 'Materia', 'Grados', 'Estado']}>
            {profile.courses.map((c) => (
              <Row key={c.courseId}>
                <span>
                  {c.discoverable ? (
                    <Link to={`/courses/${encodeURIComponent(c.courseId)}`}>{c.title}</Link>
                  ) : (
                    c.title
                  )}
                </span>
                <span>{c.subject}</span>
                <span className="mut">{c.gradeLevel.join(', ') || '—'}</span>
                <span>
                  <Pill tone={c.discoverable ? 'ok' : 'neu'}>{c.status}</Pill>
                </span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Section
        title="Compromisos vendidos"
        count={profile.enrollments.length}
        subtitle="Inscripciones activas. Se imparten pase lo que pase con la calificación."
      >
        {profile.enrollments.length === 0 ? (
          <Empty icon="🎒" title="Sin inscripciones activas" />
        ) : (
          <Table columns="1fr 1fr 160px" head={['Inscripción', 'Curso', 'Desde']}>
            {profile.enrollments.map((e) => (
              <Row key={e.enrollmentId}>
                <span>
                  <Link to={`/admin/support/case/${encodeURIComponent(e.enrollmentId)}`}>
                    {e.enrollmentId}
                  </Link>
                </span>
                <span className="mut">{e.courseId}</span>
                <span className="mut">{fmt(e.enrollmentDate)}</span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Section
        title="Cancelaciones y ausencias"
        count={incidents.history.length}
        subtitle="Cancelaciones hechas por el profesor y ausencias del profesor con reembolso registrado."
      >
        {incidents.history.length === 0 ? (
          <Empty title="Sin cancelaciones ni ausencias del profesor" />
        ) : (
          <Table columns="160px 190px 1fr 1fr" head={['Fecha', 'Tipo', 'Inscripción', 'Registro']}>
            {incidents.history.map((i) => (
              <Row key={`${i.kind}:${i.reference}`}>
                <span className="mut">{fmt(i.occurredAt)}</span>
                <span>
                  <Pill tone={i.kind === 'TEACHER_NO_SHOW' ? 'bad' : 'warn'}>
                    {INCIDENT_LABEL[i.kind]}
                  </Pill>
                </span>
                <span>
                  <Link to={`/admin/support/case/${encodeURIComponent(i.enrollmentId)}`}>
                    {i.enrollmentId}
                  </Link>
                  {i.milestoneSequence ? (
                    <span className="mut"> · sesión {i.milestoneSequence}</span>
                  ) : null}
                </span>
                <span className="mut">{i.reference}</span>
              </Row>
            ))}
          </Table>
        )}
      </Section>
    </OpsPage>
  );
};

export default AdminTeacherProfile;
